import Big from 'big.js';
import React from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { mediaSizes } from 'theme';
import { makeStyles } from '@material-ui/core/styles';

import usePaths from 'hooks/use-paths';
import useTranslation from 'hooks/use-translation';
import {
  discountAmount,
  isWeightedProduct,
  productSatisfiesSaleWeight,
  getSalesType,
  getRangeForQuantityDropdown,
} from 'shared/helpers/products';
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';
import { groupOptionsWithPrices } from 'utils/helpers/product';

import { Link, Option, Select, TextButton } from 'components/core';
import ProductImage from 'components/product-image';
import TrashCanIcon from 'src/assets/trash-can-icon';
import DiscountBadge from '../core/discount-badge';

const useQuantitySelectStyles = makeStyles({
  paper: {
    width: 55,
    marginTop: 2,
  },
  list: {
    '& li': {
      height: `35px`,
      minHeight: `0`,
      paddingLeft: `23px`,
    },
  },
});

const useWeightSelectStyles = makeStyles({
  paper: {
    width: 100,
  },
  list: {
    '& li': {
      minHeight: `0`,
    },
  },
});

export default function CartItem(props) {
  const {
    closeCheckoutModal,
    item,
    onWeightChange,
    onQuantityChange,
    onRemoveItem,
    isKiosk,
    menuType,
    dispensary,
  } = props;

  const { t } = useTranslation();

  const basePrice = useObserver(() => item.basePrice);
  const bogoDiscount = useObserver(() => item.bogoDiscount);
  const discountedBasePrice = useObserver(() => item.discountedBasePrice);
  const isMedical = useObserver(() => item.cart?.isMedical);
  const price = useObserver(() => item.price);
  const option = useObserver(() => item.option);
  const quantity = useObserver(() => item.quantity);
  const product = useObserver(() => item.product);
  const saleAdjustment = useObserver(() => item.saleAdjustment);
  const { href, route } = usePaths({ product, dispensary });

  const name = useObserver(() => product.name);
  const brand = useObserver(() => product.brand);
  const weightedProduct = useObserver(() => isWeightedProduct(product));

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`xs`));

  const isOnSale = hasSaleSpecialForMenuType({ product, medicalOrder: isMedical || false });
  const weight = option?.value || option || `N/A`;
  const salesType = getSalesType(product);
  const isWeightOnSale =
    isOnSale && product?.specialData?.saleSpecials
      ? productSatisfiesSaleWeight(salesType, product.specialData.saleSpecials, weight)
      : false;
  const weightOptions = groupOptionsWithPrices({ product, menuType, isKiosk });
  const adjustedPrice = Big(price).add(saleAdjustment || 0);
  const bogoDiscountedPrice = _.isNil(bogoDiscount) ? null : Big(adjustedPrice).minus(bogoDiscount).toFixed(2);

  const quantitiesAvailableForSelection = getRangeForQuantityDropdown({
    product,
    option,
    quantityInCart: quantity,
    isKiosk,
  });

  const quantitySelectClasses = useQuantitySelectStyles();
  const weightSelectClasses = useWeightSelectStyles();
  return (
    // eslint-disable-next-line i18next/no-literal-string
    <Container data-testid='cart-item-container'>
      <Link href={href} route={route}>
        <a href={href} onClick={closeCheckoutModal}>
          <StyledProductImage product={product} width={isMobile ? 55 : 81} height={isMobile ? 55 : 81} />
        </a>
      </Link>
      <DetailContainer>
        {bogoDiscountedPrice && (
          <DiscountBadge type='bogo' value={Big(adjustedPrice - bogoDiscountedPrice).toFixed(2)} isMobile={isMobile} />
        )}
        {!bogoDiscountedPrice && isWeightOnSale && (
          <DiscountBadge type='sale' value={discountAmount(basePrice, discountedBasePrice)} />
        )}
        <Link href={href} route={route}>
          <a href={href} onClick={closeCheckoutModal}>
            <Name>{name}</Name>
          </a>
        </Link>
        {brand?.name && <Brand>{brand?.name}</Brand>}
        {item.additionalOption && <AdditionalOptionLabel>{item.additionalOption}</AdditionalOptionLabel>}
        <WeightedRemove>
          {weightedProduct && (
            <WeightSelect
              SelectDisplayProps={{
                'aria-label': t('common.weight', 'Weight'),
              }}
              onChange={onWeightChange}
              value={option}
              mr='12px'
              disabled={weightOptions.length < 2}
              MenuProps={{ classes: { paper: weightSelectClasses.paper, list: weightSelectClasses.list } }}
            >
              {_.map(weightOptions, ({ label, value }) => (
                <Option key={value} value={value}>
                  <OptionText>{label}</OptionText>
                </Option>
              ))}
            </WeightSelect>
          )}
          {weightedProduct && <Separator />}
          <RemoveButton onClick={onRemoveItem} href='#'>
            <TrashCanIcon />
            {t('common.remove', 'Remove')}
          </RemoveButton>
        </WeightedRemove>
      </DetailContainer>

      <OptionContainer>
        <QuantitySelect
          SelectDisplayProps={{
            'aria-label': t('common.quantity', 'Quantity'),
          }}
          onChange={onQuantityChange}
          value={quantity}
          MenuProps={{ classes: { paper: quantitySelectClasses.paper, list: quantitySelectClasses.list } }}
        >
          {_.map(quantitiesAvailableForSelection, (num) => (
            <Option key={num} value={num}>
              <OptionText>{num}</OptionText>
            </Option>
          ))}
        </QuantitySelect>
        <Price showPriceDiscount={!!bogoDiscountedPrice || !!isWeightOnSale}>
          <div>${Big(basePrice * quantity).toFixed(2)}</div>
          {bogoDiscountedPrice && <div>${Big(bogoDiscountedPrice).toFixed(2)}</div>}
          {isWeightOnSale && !bogoDiscountedPrice && <div>${Big(adjustedPrice).toFixed(2)}</div>}
        </Price>
      </OptionContainer>
    </Container>
  );
}

const AdditionalOptionLabel = styled.p`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 12px;
  line-height: 165%;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin-top: 3px;
    font-size: 13px;
  }
`;

const WeightedRemove = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const OptionText = styled.div`
  max-width: 50px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RemoveButton = styled(TextButton)`
  color: ${({ theme }) => theme.colors.grey[45]};
  font-size: 12px;
  display: flex;
  align-items: center;

  > svg {
    margin-right: 6px;
    position: relative;
  }
`;

const Separator = styled.div`
  border-left: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  content: '';
  height: 19px;
  width: 1px;
  margin: 0 10px;
`;

const WeightSelect = styled(Select)`
  border: 0 !important;

  > div {
    font-size: 13px !important;
    padding: 0 !important;
    height: auto !important;
    padding-right: 18px !important;
  }

  > svg {
    right: -7px;
    height: 7px;
    width: 11px;
    visibility: ${({ disabled }) => (disabled ? `hidden` : `inherit`)};
  }
`;

const QuantitySelect = styled(Select)`
  height: 32px;
  width: 55px;

  .MuiSelect-root {
    position: relative;
    left: -7px;
  }

  > svg {
    width: 10px;
  }
`;

const Container = styled.div`
  display: flex;
  margin: 7px 25px;
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  a,
  img {
    cursor: pointer;
  }

  &:first-of-type {
    margin-top: 0;
  }

  &:last-of-type {
    border-bottom: 0;
  }

  @media (min-width: 450px) {
    margin: 7px 25px;
  }

  @media (min-width: 600px) {
    margin: 7px 30px;
  }

  @media (max-width: ${mediaSizes.phone}px) {
    padding: 6px 0 10px 0;
    img {
      margin-top: 5px;
    }
  }
`;

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0%;
  margin: 12px 9px;

  @media (max-width: ${mediaSizes.phone}px) {
    margin: 9px;
  }
`;

const Name = styled.strong`
  color: ${({ theme }) => theme.colors.primaryBlack};
  font-size: 14px;
  line-height: 14px;
  margin-top: 4px;
`;

const Brand = styled.p`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 12px;
  line-height: 165%;

  @media (max-width: ${mediaSizes.phone}px) {
    margin-top: 3px;
    font-size: 13px;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  margin: 12px 0;
  align-items: flex-start;
`;

const Price = styled.div`
  color: ${({ theme }) => theme.colors.grey[30]};
  font-size: 14px;
  line-height: 175%;
  margin-left: 15px;
  margin-top: 4px;
  min-width: 60px;
  text-align: right;

  ${({ showPriceDiscount, theme }) =>
    showPriceDiscount &&
    `
    padding: 0 8px;

    > div:first-child {
      color: ${theme.colors.grey[60]};
      margin-top: 2px;
      text-decoration: line-through;
    }
  `};

  @media (min-width: 600px) {
    margin-left: 18px;
  }

  @media (max-width: ${mediaSizes.phone}px) {
    min-width: 50px;
  }
`;

const StyledProductImage = styled(ProductImage)`
  @media (max-width: ${mediaSizes.smallPhone}px) {
    display: none;
  }
`;
